import { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { NavIcon, useBrandTokens, SidebarToggleButton } from '../../../shell/shared';
import { usePatientShellContext } from '../../../shell/PatientShellLayout';
import { useAuth } from '@helios/auth-firebase-web';
import { FilesTab } from '../consultation/FilesTab';
import { InvoicesTab } from '../consultation/InvoicesTab';
import { EncounterFormsTab } from './EncounterFormsTab';
import { EncounterVitalsTab } from './EncounterVitalsTab';
import { EncounterNotesTab } from './EncounterNotesTab';
import { EncounterChartingTab } from './EncounterChartingTab';
import { EncounterPrescriptionsTab } from './EncounterPrescriptionsTab';
import { EncounterPharmacyTab } from './EncounterPharmacyTab';
import { usePowerSyncQuery, usePowerSyncDb } from '../../../adapters/powersync';
import {
  qEncounterById,
  qAppointmentById,
  qPatientWorkspaceSettings,
  mapPatientWorkspaceSettingsToUI,
  type PatientEncounterRow,
  type AppointmentRow,
  type PatientWorkspaceSettingsRow,
} from '@helios/data-access';
import { useEncounterTabConfig } from './useEncounterTabConfig';
import { VisitNoteSettingsDrawer } from './VisitNoteSettingsDrawer';
import { GuidedNoteRenderer } from './GuidedNoteRenderer';
import { DentalChartingHero } from './DentalChartingHero';
import { AestheticVisualHero } from './AestheticVisualHero';
import { VisitBillingChip } from './VisitBillingChip';
import { VisitBillingPanel } from './VisitBillingPanel';
import { TreatmentPlanSection } from './TreatmentPlanSection';
import { GuidedInvoiceSummary } from './GuidedInvoiceSummary';
import { GuidedPaymentSummary } from './GuidedPaymentSummary';
import { DeleteEncounterModal } from './DeleteEncounterModal';
import { deleteEncounterCascade } from '../../../services/delete-encounter-service';

// ─── Helpers ───

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

const TYPE_LABELS: Record<string, string> = {
  consultation: 'Consultation',
  procedure: 'Procedure',
  follow_up: 'Follow-up',
  triage: 'Triage',
};

// ─── Tabs ───

type TabKey =
  | 'summary'
  | 'charting'
  | 'notes'
  | 'vitals'
  | 'prescriptions'
  | 'treatment_plan'
  | 'forms'
  | 'files'
  | 'invoices'
  | 'pharmacy'
  | 'payments';

// ─── Status Badge ───

type DisplayStatus = 'Open' | 'Closed' | 'In Progress';

const STATUS_STYLES: Record<DisplayStatus, { bg: string; color: string }> = {
  Open: { bg: 'var(--color-success-bg)', color: 'var(--color-success-text)' },
  Closed: { bg: 'var(--color-neutral-bg)', color: 'var(--text-muted)' },
  'In Progress': { bg: 'var(--color-info-bg)', color: 'var(--color-info)' },
};

function StatusBadge({ status }: { status: string }) {
  const display: DisplayStatus = status === 'closed' ? 'Closed' : 'Open';
  const s = STATUS_STYLES[display];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 'var(--radius-full)',
        background: s.bg,
        color: s.color,
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-semi)',
        lineHeight: '20px',
      }}
    >
      {display}
    </span>
  );
}

// ─── Summary Tab ───

// ─── Accordion icon map ───

const ACCORDION_ICONS: Record<string, string> = {
  summary: 'layout-grid',
  vitals: 'heart',
  charting: 'edit',
  notes: 'file-text',
  prescriptions: 'clipboard',
  treatment_plan: 'clipboard',
  forms: 'file-text',
  files: 'folder-open',
  invoices: 'credit-card',
  pharmacy: 'heart',
  payments: 'credit-card',
};

// ─── Summary — Patient-Overview-aligned styles ───

const S_COL: React.CSSProperties = {
  flex: '1 1 0',
  minWidth: 180,
  borderTop: '3px solid var(--border)',
  paddingTop: 'var(--sp-3)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-2)',
};

const S_GROUP_TITLE: React.CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  color: 'var(--color-primary)',
  margin: 0,
};

const S_LABEL: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--text-muted)',
};

const S_VALUE: React.CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text)',
  fontWeight: 500,
};

const S_ROW: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
};

function SummaryTab({
  encounter,
  appointment,
}: {
  encounter: PatientEncounterRow;
  appointment?: AppointmentRow | null;
}) {
  const duration =
    encounter.ended_at && encounter.started_at
      ? `${Math.round((new Date(encounter.ended_at).getTime() - new Date(encounter.started_at).getTime()) / 60000)} min`
      : 'Ongoing';

  return (
    <div
      style={{
        padding: 'var(--sp-4) var(--sp-5)',
        display: 'flex',
        gap: 'var(--sp-5)',
        flexWrap: 'wrap',
      }}
    >
      {/* Column 1 — Visit Details */}
      <div style={{ ...S_COL, borderTopColor: 'var(--color-primary)' }}>
        <h4 style={S_GROUP_TITLE}>Visit Details</h4>
        <div style={S_ROW}>
          <span style={S_LABEL}>Date</span>
          <span style={S_VALUE}>{formatDate(encounter.started_at)}</span>
        </div>
        <div style={S_ROW}>
          <span style={S_LABEL}>Time</span>
          <span style={S_VALUE}>{formatTime(encounter.started_at) || '—'}</span>
        </div>
        <div style={S_ROW}>
          <span style={S_LABEL}>Duration</span>
          <span style={S_VALUE}>{duration}</span>
        </div>
        <div style={S_ROW}>
          <span style={S_LABEL}>Type</span>
          <span style={S_VALUE}>{TYPE_LABELS[encounter.type] ?? encounter.type}</span>
        </div>
        <div style={S_ROW}>
          <span style={S_LABEL}>Provider</span>
          <span style={S_VALUE}>{encounter.provider_name ?? 'Unassigned'}</span>
        </div>
      </div>

      {/* Column 2 — Appointment (conditional) or Chief Complaint */}
      {appointment ? (
        <div style={{ ...S_COL, borderTopColor: 'var(--text-muted)' }}>
          <h4 style={{ ...S_GROUP_TITLE, color: 'var(--text-muted)' }}>Appointment</h4>
          <div style={S_ROW}>
            <span style={S_LABEL}>Scheduled</span>
            <span style={S_VALUE}>
              {appointment.date ?? '—'} · {appointment.start_time ?? '—'}
            </span>
          </div>
          {appointment.duration_minutes != null && (
            <div style={S_ROW}>
              <span style={S_LABEL}>Duration</span>
              <span style={S_VALUE}>{appointment.duration_minutes} min</span>
            </div>
          )}
          {appointment.notes && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={S_LABEL}>Notes</span>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text)' }}>
                {appointment.notes}
              </span>
            </div>
          )}
        </div>
      ) : null}

      {/* Column 3 (or 2) — Chief Complaint */}
      <div style={{ ...S_COL, borderTopColor: 'var(--text-muted)' }}>
        <h4 style={{ ...S_GROUP_TITLE, color: 'var(--text-muted)' }}>Chief Complaint</h4>
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
            fontStyle: 'italic',
          }}
        >
          No chief complaint recorded
        </span>
      </div>
    </div>
  );
}

// ─── Placeholder Tabs ───

const PLACEHOLDER_MESSAGES: Record<string, string> = {
  'Treatment Plan': 'No treatment plan yet',
  Payments: 'No payments recorded',
};

function PlaceholderTab({ title }: { title: string }) {
  return (
    <div
      style={{
        padding: 'var(--sp-8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        color: 'var(--text-muted)',
        fontSize: 'var(--font-size-base)',
      }}
    >
      {PLACEHOLDER_MESSAGES[title] ?? `${title} content will appear here`}
    </div>
  );
}

// ─── Main Component ───

// ─── Main ───

export function EncounterDetailPage() {
  const { id: patientId, encounterId } = useParams<{ id: string; encounterId: string }>();
  const navigate = useNavigate();
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { collapsed: sidebarCollapsed, onToggleSidebar } = usePatientShellContext();

  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as TabKey | null;
  const [activeTab, setActiveTab] = useState<TabKey>(tabParam ?? 'summary');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [billingPanelOpen, setBillingPanelOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { db } = usePowerSyncDb();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? '';
  const {
    settings: tabSettings,
    visibleTabs: userVisibleTabs,
    layoutConfig,
    updateSettings,
    resetToDefault,
  } = useEncounterTabConfig(orgId, userId);

  // Clinic-level Patient Consultation Tabs visibility (PRD.181). Hides
  // sections from every user in the clinic regardless of their per-user
  // encounter tab config. Falls back to "all visible" until the workspace
  // settings row arrives so the UI never blanks out during initial load.
  const { data: workspaceSettingsRows = [] } = usePowerSyncQuery<PatientWorkspaceSettingsRow>(
    qPatientWorkspaceSettings(),
  );
  const clinicVisibleConsultKeys = useMemo<Set<string> | null>(() => {
    if (workspaceSettingsRows.length === 0) return null;
    const ui = mapPatientWorkspaceSettingsToUI(workspaceSettingsRows[0]);
    const allowed = new Set<string>();
    if (ui.consultShowSummary) allowed.add('summary');
    if (ui.consultShowVitals) allowed.add('vitals');
    if (ui.consultShowCharting) allowed.add('charting');
    if (ui.consultShowNotes) allowed.add('notes');
    if (ui.consultShowPrescriptions) allowed.add('prescriptions');
    if (ui.consultShowTreatmentPlan) allowed.add('treatment_plan');
    if (ui.consultShowForms) allowed.add('forms');
    if (ui.consultShowFiles) allowed.add('files');
    if (ui.consultShowInvoices) allowed.add('invoices');
    if (ui.consultShowPayments) allowed.add('payments');
    // PRD.201 — Pharmacy is always allowed at the clinic level for now;
    // there is no `consult_show_pharmacy` column in patient_workspace_settings
    // yet, and the per-user tab config still controls visibility per role.
    allowed.add('pharmacy');
    return allowed;
  }, [workspaceSettingsRows]);

  // Intersect the user's per-tab config with the clinic visibility. If the
  // clinic settings haven't loaded yet, fall back to the user's config.
  const visibleTabs = useMemo(
    () =>
      clinicVisibleConsultKeys
        ? userVisibleTabs.filter((t) => clinicVisibleConsultKeys.has(t.key))
        : userVisibleTabs,
    [userVisibleTabs, clinicVisibleConsultKeys],
  );
  const [openSections, setOpenSections] = useState<Set<string>>(() => {
    const def = layoutConfig.defaultOpenSection;
    if (def === '__all__') return new Set(visibleTabs.map((t) => t.key));
    return new Set(def ? [def] : ['summary']);
  });

  // Sync open sections when layout settings change (e.g. after Save in drawer)
  useEffect(() => {
    const def = layoutConfig.defaultOpenSection;
    if (def === '__all__') {
      setOpenSections(new Set(visibleTabs.map((t) => t.key)));
    } else if (def) {
      setOpenSections(new Set([def]));
    }
  }, [layoutConfig.defaultOpenSection, layoutConfig.accordionMode]);

  // Keep the UI in sync with the URL's ?tab= hint. Needed because the shell
  // can keep this page mounted across navigations to the same encounter (e.g.
  // clicking an invoice row in the sidebar while already on that encounter)
  // — without this, the state would stay on whatever tab/section was last
  // shown and the hint in the URL would be ignored. Handles both layout
  // styles: flipping the active tab and expanding the matching accordion
  // section.
  useEffect(() => {
    if (!tabParam) return;
    setActiveTab(tabParam);
    if (layoutConfig.style === 'accordion') {
      setOpenSections((prev) => {
        if (prev.has(tabParam)) return prev;
        if (layoutConfig.accordionMode === 'single') return new Set([tabParam]);
        const next = new Set(prev);
        next.add(tabParam);
        return next;
      });
      // Scroll the section into view after the expansion has painted.
      const t = window.setTimeout(() => {
        const el = document.getElementById(`encounter-section-${tabParam}`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
      return () => window.clearTimeout(t);
    }
  }, [tabParam, layoutConfig.style, layoutConfig.accordionMode]);

  // Safe fallback: if the active tab gets hidden by the clinic's Patient
  // Consultation Tabs settings (PRD.181), reroute to the first remaining
  // visible tab so the user never lands on a blank section.
  useEffect(() => {
    if (visibleTabs.length === 0) return;
    if (visibleTabs.some((t) => t.key === activeTab)) return;
    setActiveTab(visibleTabs[0].key as TabKey);
  }, [activeTab, visibleTabs]);

  const { data: encounterRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    qEncounterById(encounterId ?? ''),
  );
  const encounter = encounterRows[0] ?? null;

  // Fetch linked appointment (if any) for appointment-aware Summary
  const appointmentId = encounter?.appointment_id ?? '';
  const { data: appointmentRows = [] } = usePowerSyncQuery<AppointmentRow>(
    qAppointmentById(appointmentId),
  );
  const appointment = appointmentId ? (appointmentRows[0] ?? null) : null;

  const handleDeleteEncounter = useCallback(
    async (opts: { deleteAppointment: boolean; deleteQueueEntry: boolean }) => {
      if (!db || !encounterId || !orgId) return;
      setIsDeleting(true);
      try {
        const result = await deleteEncounterCascade({
          db,
          orgId,
          encounterId,
          deleteAppointment: opts.deleteAppointment,
          deleteQueueEntry: opts.deleteQueueEntry,
        });
        setDeleteModalOpen(false);
        // Build toast message
        const parts = ['Encounter deleted'];
        if (result.deletedAppointment) parts.push('+ appointment');
        if (result.deletedQueueEntry) parts.push('+ queue entry');
        if (result.error) parts.push(`(${result.error})`);
        console.log('[DeleteEncounter]', parts.join(' '));
        // Navigate back
        navigate(`/patient/${patientId}/encounters`);
      } catch (err) {
        console.error('[DeleteEncounter] Failed:', err);
      } finally {
        setIsDeleting(false);
      }
    },
    [db, orgId, encounterId, patientId, navigate],
  );

  if (!encounter) {
    return (
      <div
        style={{
          padding: 'var(--sp-8)',
          textAlign: 'center',
          color: 'var(--text-muted)',
        }}
      >
        Visit note not found
      </div>
    );
  }

  const typeLabel = TYPE_LABELS[encounter.type] ?? encounter.type;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '0 var(--sp-8)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        {/* Single row: sidebar toggle + breadcrumb + title + status + subtitle + menu */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 48,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* Sidebar toggle */}
            <SidebarToggleButton collapsed={sidebarCollapsed} onToggle={onToggleSidebar} />

            {/* Breadcrumb: Patient Overview >> Visit Note */}
            <nav style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button
                onClick={() => navigate(`/patient/${patientId}/encounters`)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: primary,
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 500,
                  cursor: 'pointer',
                  padding: 0,
                  whiteSpace: 'nowrap',
                  textDecoration: 'none',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.textDecoration = 'underline';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textDecoration = 'none';
                }}
              >
                Patient Overview
              </button>
              <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>
                <NavIcon name="chevron-right" />
              </span>
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  color: 'var(--text)',
                  whiteSpace: 'nowrap',
                }}
              >
                Visit Note
              </span>
            </nav>

            <StatusBadge status={encounter.status} />

            {/* Subtitle */}
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-muted)',
                whiteSpace: 'nowrap',
              }}
            >
              {typeLabel} · {formatDate(encounter.started_at)} · {formatTime(encounter.started_at)}{' '}
              · {encounter.provider_name ?? 'Unassigned'}
            </span>
          </div>

          <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
            {/* Billing chip — guided note only (PRD.163) */}
            {layoutConfig.style === 'guided_note' && (
              <VisitBillingChip
                patientId={patientId ?? ''}
                encounterId={encounterId ?? ''}
                onClick={() => setBillingPanelOpen(true)}
              />
            )}
            {/* Settings button */}
            <button
              onClick={() => setSettingsOpen(true)}
              title="Visit Note Settings"
              style={{
                width: 32,
                height: 32,
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-muted)',
                flexShrink: 0,
              }}
            >
              <NavIcon name="gear" />
            </button>
            {/* Delete button */}
            <button
              onClick={() => setDeleteModalOpen(true)}
              title="Delete Visit Note"
              style={{
                width: 32,
                height: 32,
                borderRadius: 'var(--radius-sm)',
                border: '1px solid #fecaca',
                background: '#fef2f2',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#dc2626',
                flexShrink: 0,
              }}
            >
              <NavIcon name="trash" />
            </button>
          </div>
        </div>

        {/* Tabs — only in tabs mode */}
        {layoutConfig.style === 'tabs' && (
          <div
            style={{
              display: 'flex',
              gap: 0,
              marginTop: 12,
              alignItems: 'center',
            }}
          >
            {visibleTabs.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as TabKey)}
                  style={{
                    padding: '8px 16px',
                    background: 'none',
                    border: 'none',
                    borderBottom: isActive ? `2px solid ${primary}` : '2px solid transparent',
                    color: isActive ? 'var(--text)' : 'var(--text-muted)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: isActive ? 'var(--font-weight-semi)' : 'var(--font-weight-medium)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Content area */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {layoutConfig.style === 'tabs' ? (
          // ── Tabs mode ──
          <>
            {activeTab === 'summary' && (
              <SummaryTab encounter={encounter} appointment={appointment} />
            )}
            {activeTab === 'charting' && (
              <EncounterChartingTab
                patientId={patientId ?? ''}
                encounterId={encounterId ?? ''}
                visitId={encounter.visit_id}
              />
            )}
            {activeTab === 'notes' && (
              <EncounterNotesTab
                patientId={patientId ?? ''}
                encounterId={encounterId ?? ''}
                visitId={encounter.visit_id}
              />
            )}
            {activeTab === 'vitals' && (
              <EncounterVitalsTab patientId={patientId ?? ''} encounterId={encounterId ?? ''} />
            )}
            {activeTab === 'prescriptions' && (
              <EncounterPrescriptionsTab
                patientId={patientId ?? ''}
                encounterId={encounterId ?? ''}
                visitId={encounter.visit_id}
              />
            )}
            {activeTab === 'treatment_plan' && <PlaceholderTab title="Treatment Plan" />}
            {activeTab === 'forms' && (
              <div style={{ padding: 'var(--sp-5)' }}>
                <EncounterFormsTab
                  patientId={patientId ?? ''}
                  encounterId={encounterId ?? ''}
                  visitId={encounter.visit_id}
                />
              </div>
            )}
            {activeTab === 'files' && (
              <div style={{ padding: 'var(--sp-5)' }}>
                <FilesTab
                  patientId={patientId ?? ''}
                  encounterId={encounterId}
                  visitId={encounter.visit_id ?? undefined}
                />
              </div>
            )}
            {activeTab === 'invoices' && (
              <div style={{ padding: 'var(--sp-5)' }}>
                <InvoicesTab
                  patientId={patientId ?? ''}
                  encounterId={encounterId}
                  visitId={encounter.visit_id}
                  initialInvoiceId={searchParams.get('invoiceId') ?? undefined}
                />
              </div>
            )}
            {activeTab === 'pharmacy' && (
              <EncounterPharmacyTab patientId={patientId ?? ''} encounterId={encounterId ?? ''} />
            )}
            {activeTab === 'payments' && <PlaceholderTab title="Payments" />}
          </>
        ) : layoutConfig.style === 'guided_note' ? (
          // ── Guided Note mode ──
          <GuidedNoteRenderer
            preset={layoutConfig.guidedNotePreset}
            visibleTabs={visibleTabs}
            primary={primary}
            renderHeroSection={(cardId) => {
              if (cardId === 'charting-images') {
                return (
                  <DentalChartingHero
                    patientId={patientId ?? ''}
                    encounterId={encounterId ?? ''}
                    visitId={encounter.visit_id}
                    primary={primary}
                  />
                );
              }
              if (cardId === 'visual-assessment') {
                return (
                  <AestheticVisualHero
                    patientId={patientId ?? ''}
                    encounterId={encounterId ?? ''}
                    visitId={encounter.visit_id}
                    primary={primary}
                  />
                );
              }
              return undefined;
            }}
            renderSubmodule={(key) => {
              switch (key) {
                case 'summary':
                  return <SummaryTab encounter={encounter} appointment={appointment} />;
                case 'charting':
                  return (
                    <EncounterChartingTab
                      patientId={patientId ?? ''}
                      encounterId={encounterId ?? ''}
                      visitId={encounter.visit_id}
                    />
                  );
                case 'notes':
                  return (
                    <EncounterNotesTab
                      patientId={patientId ?? ''}
                      encounterId={encounterId ?? ''}
                      visitId={encounter.visit_id}
                      hideHeader
                      quietActions
                    />
                  );
                case 'vitals':
                  return (
                    <EncounterVitalsTab
                      patientId={patientId ?? ''}
                      encounterId={encounterId ?? ''}
                    />
                  );
                case 'prescriptions':
                  return (
                    <EncounterPrescriptionsTab
                      patientId={patientId ?? ''}
                      encounterId={encounterId ?? ''}
                      visitId={encounter.visit_id}
                      hideHeader
                      quietActions
                    />
                  );
                case 'treatment_plan':
                  return <TreatmentPlanSection primary={primary} />;
                case 'forms':
                  return (
                    <div style={{ padding: 'var(--sp-5)' }}>
                      <EncounterFormsTab
                        patientId={patientId ?? ''}
                        encounterId={encounterId ?? ''}
                        visitId={encounter.visit_id}
                        quietActions
                      />
                    </div>
                  );
                case 'files':
                  return (
                    <div style={{ padding: 'var(--sp-5)' }}>
                      <FilesTab
                        patientId={patientId ?? ''}
                        encounterId={encounterId}
                        visitId={encounter.visit_id ?? undefined}
                      />
                    </div>
                  );
                case 'invoices':
                  return (
                    <GuidedInvoiceSummary
                      patientId={patientId ?? ''}
                      encounterId={encounterId ?? ''}
                      visitId={encounter.visit_id}
                    />
                  );
                case 'pharmacy':
                  return (
                    <EncounterPharmacyTab
                      patientId={patientId ?? ''}
                      encounterId={encounterId ?? ''}
                      hideHeader
                    />
                  );
                case 'payments':
                  return (
                    <GuidedPaymentSummary
                      patientId={patientId ?? ''}
                      encounterId={encounterId ?? ''}
                    />
                  );
                default:
                  return <PlaceholderTab title={key} />;
              }
            }}
          />
        ) : (
          // ── Accordion mode ──
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {visibleTabs.map((tab) => {
              const isOpen = openSections.has(tab.key);
              const icon = ACCORDION_ICONS[tab.key] ?? 'file-text';
              const toggleSection = () => {
                setOpenSections((prev) => {
                  const next = new Set(prev);
                  if (next.has(tab.key)) {
                    next.delete(tab.key);
                  } else {
                    if (layoutConfig.accordionMode === 'single') {
                      next.clear();
                    }
                    next.add(tab.key);
                  }
                  return next;
                });
              };
              return (
                <div key={tab.key} id={`encounter-section-${tab.key}`}>
                  {/* Accordion header bar */}
                  <button
                    onClick={toggleSection}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      width: '100%',
                      padding: '14px var(--sp-6)',
                      background: isOpen ? 'var(--bg-surface-2)' : 'transparent',
                      border: 'none',
                      borderBottom: '1px solid var(--border)',
                      borderTop: '1px solid var(--border)',
                      marginTop: -1,
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background 150ms',
                    }}
                    onMouseEnter={(e) => {
                      if (!isOpen) e.currentTarget.style.background = 'var(--bg-surface-2)';
                    }}
                    onMouseLeave={(e) => {
                      if (!isOpen) e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={isOpen ? primary : 'var(--text-muted)'}
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform 150ms',
                        flexShrink: 0,
                      }}
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                    <span
                      style={{
                        color: isOpen ? primary : 'var(--text-muted)',
                        flexShrink: 0,
                        display: 'flex',
                      }}
                    >
                      <NavIcon name={icon} />
                    </span>
                    <span
                      style={{
                        flex: 1,
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 600,
                        color: isOpen ? 'var(--text)' : 'var(--text-muted)',
                        letterSpacing: '-0.1px',
                      }}
                    >
                      {tab.label}
                    </span>
                    {!isOpen && (
                      <span
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--text-placeholder)',
                        }}
                      >
                        Click to expand
                      </span>
                    )}
                  </button>
                  {/* Accordion content */}
                  {isOpen && (
                    <div style={{ borderBottom: '2px solid var(--border)' }}>
                      {tab.key === 'summary' && (
                        <SummaryTab encounter={encounter} appointment={appointment} />
                      )}
                      {tab.key === 'charting' && (
                        <EncounterChartingTab
                          patientId={patientId ?? ''}
                          encounterId={encounterId ?? ''}
                          visitId={encounter.visit_id}
                        />
                      )}
                      {tab.key === 'notes' && (
                        <EncounterNotesTab
                          patientId={patientId ?? ''}
                          encounterId={encounterId ?? ''}
                          visitId={encounter.visit_id}
                        />
                      )}
                      {tab.key === 'vitals' && (
                        <EncounterVitalsTab
                          patientId={patientId ?? ''}
                          encounterId={encounterId ?? ''}
                        />
                      )}
                      {tab.key === 'prescriptions' && (
                        <EncounterPrescriptionsTab
                          patientId={patientId ?? ''}
                          encounterId={encounterId ?? ''}
                          visitId={encounter.visit_id}
                        />
                      )}
                      {tab.key === 'treatment_plan' && <PlaceholderTab title="Treatment Plan" />}
                      {tab.key === 'forms' && (
                        <div style={{ padding: 'var(--sp-5)' }}>
                          <EncounterFormsTab
                            patientId={patientId ?? ''}
                            encounterId={encounterId ?? ''}
                            visitId={encounter.visit_id}
                          />
                        </div>
                      )}
                      {tab.key === 'files' && (
                        <div style={{ padding: 'var(--sp-5)' }}>
                          <FilesTab
                            patientId={patientId ?? ''}
                            encounterId={encounterId}
                            visitId={encounter.visit_id ?? undefined}
                          />
                        </div>
                      )}
                      {tab.key === 'invoices' && (
                        <div style={{ padding: 'var(--sp-5)' }}>
                          <InvoicesTab
                            patientId={patientId ?? ''}
                            encounterId={encounterId}
                            visitId={encounter.visit_id}
                            initialInvoiceId={searchParams.get('invoiceId') ?? undefined}
                          />
                        </div>
                      )}
                      {tab.key === 'pharmacy' && (
                        <EncounterPharmacyTab
                          patientId={patientId ?? ''}
                          encounterId={encounterId ?? ''}
                          hideHeader
                        />
                      )}
                      {tab.key === 'payments' && <PlaceholderTab title="Payments" />}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <VisitNoteSettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={tabSettings}
        onSave={updateSettings}
        onReset={resetToDefault}
        primary={primary}
        clinicVisibleConsultKeys={clinicVisibleConsultKeys}
      />

      <VisitBillingPanel
        open={billingPanelOpen}
        onClose={() => setBillingPanelOpen(false)}
        patientId={patientId ?? ''}
        encounterId={encounterId ?? ''}
        visitId={encounter.visit_id}
      />

      <DeleteEncounterModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteEncounter}
        hasLinkedAppointment={!!encounter.appointment_id}
        hasLinkedQueueEntry={!!encounter.queue_id}
        isDeleting={isDeleting}
      />
    </div>
  );
}
